/**
 * SpacePageContext
 *
 * Scoped route context for one active Space page.
 *
 * Primary responsibilities:
 * - Provide the current Space, member list, and selected item
 * - Expose shared item-open and refresh helpers to Space route consumers
 *
 * Dependencies:
 * - React context
 * - Space item detail helpers
 */

"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction
} from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { ChatbotUIContext } from "@/context/context"
import { isSpaceAuthRequestError } from "@/db/spaces"
import { Tables } from "@/supabase/types"
import {
  spaceDetailQueryOptions,
  spaceActivitiesQueryOptions,
  spaceHierarchyQueryOptions,
  spaceItemDetailQueryOptions,
  spaceMembersQueryOptions,
  spacesQueryKeys,
  spaceWhiteboardsQueryOptions
} from "@/lib/spaces/queries"
import {
  type SpaceItemDetailData,
  type SpaceItemDetailTarget,
  toSelectedSpaceItem
} from "@/lib/spaces/item-details"
import { isSpaceQueryCancelledError } from "@/lib/spaces/query-errors"
import { upsertSpaceIntoKnownList } from "@/lib/spaces/state"
import type {
  SelectedSpaceItem,
  SpaceMemberWithProfile,
  SpaceSelectableItemType
} from "@/types/spaces"

interface SpacePageContextValue {
  spaceId: string
  space: Tables<"spaces"> | null
  members: SpaceMemberWithProfile[]
  selectedItem: SelectedSpaceItem
  isLoading: boolean
  isAuthError: boolean
  errorMessage: string | null
  setSelectedItem: Dispatch<SetStateAction<SelectedSpaceItem>>
  openItem: (
    item: SpaceItemDetailTarget<SpaceSelectableItemType>
  ) => Promise<SelectedSpaceItem | null>
  refreshSpace: () => Promise<Tables<"spaces"> | null>
}

const SpacePageContext = createContext<SpacePageContextValue | null>(null)

const getSelectedItemKey = (item: SelectedSpaceItem) => {
  return item ? `${item.type}:${item.id}` : null
}

interface SpacePageProviderProps {
  children: ReactNode
  value: SpacePageContextValue
}

export const SpacePageProvider = ({
  children,
  value
}: SpacePageProviderProps) => {
  return (
    <SpacePageContext.Provider value={value}>
      {children}
    </SpacePageContext.Provider>
  )
}

interface SpaceRouteProviderProps {
  children: ReactNode
  mirrorGlobalSelection?: boolean
  spaceId: string
}

export const SpaceRouteProvider = ({
  children,
  mirrorGlobalSelection = true,
  spaceId
}: SpaceRouteProviderProps) => {
  const {
    selectedSpace,
    selectedSpaceItem,
    setSelectedSpace,
    selectedWorkspace,
    setSelectedWorkspace,
    spaces,
    workspaces,
    setSpaceMembers,
    setSelectedSpaceItem: setMirroredSelectedSpaceItem,
    setSpaces
  } = useContext(ChatbotUIContext)
  const [selectedItemState, setSelectedItemState] =
    useState<SelectedSpaceItem>(() =>
      mirrorGlobalSelection && selectedSpaceItem?.data?.space_id === spaceId
        ? selectedSpaceItem
        : null
    )
  const hasTakenSelectionControlRef = useRef(
    Boolean(mirrorGlobalSelection && selectedSpaceItem?.data?.space_id === spaceId)
  )
  const selectedItemRef = useRef(selectedItemState)
  const selectedItemVersionRef = useRef(0)
  const previousSpaceIdRef = useRef(spaceId)
  const workspaceSyncSpaceIdRef = useRef<string | null>(null)
  const queryClient = useQueryClient()
  const spaceQuery = useQuery({
    ...spaceDetailQueryOptions(spaceId),
    enabled: Boolean(spaceId)
  })
  const membersQuery = useQuery({
    ...spaceMembersQueryOptions(spaceId),
    enabled: Boolean(spaceId)
  })
  const mirroredSpace =
    selectedSpace?.id === spaceId ? selectedSpace : null
  const availableSpaces = spaces ?? []
  const listedSpace =
    availableSpaces.find((space) => space.id === spaceId) ?? null
  const currentSpace = spaceQuery.data ?? mirroredSpace ?? listedSpace
  const currentMembers = membersQuery.data ?? []
  const isLoading =
    (spaceQuery.isLoading || spaceQuery.isFetching) && !currentSpace
  const isAuthError =
    !currentSpace &&
    !spaceQuery.isFetching &&
    isSpaceAuthRequestError(spaceQuery.error)
  const errorMessage =
    !currentSpace &&
    !spaceQuery.isFetching &&
    spaceQuery.error instanceof Error
      ? spaceQuery.error.message
      : null

  useEffect(() => {
    selectedItemRef.current = selectedItemState
  }, [selectedItemState])

  useEffect(() => {
    if (!mirrorGlobalSelection) {
      return
    }

    if (
      !hasTakenSelectionControlRef.current &&
      !selectedItemState &&
      selectedSpaceItem?.data?.space_id === spaceId
    ) {
      return
    }

    setMirroredSelectedSpaceItem(selectedItemState)
  }, [
    mirrorGlobalSelection,
    selectedItemState,
    selectedSpaceItem,
    setMirroredSelectedSpaceItem,
    spaceId
  ])

  useEffect(() => {
    if (!mirrorGlobalSelection) {
      return
    }

    if (
      hasTakenSelectionControlRef.current ||
      selectedItemState ||
      !selectedSpaceItem
    ) {
      return
    }

    if (selectedSpaceItem.data.space_id !== spaceId) {
      return
    }

    hasTakenSelectionControlRef.current = true
    setSelectedItemState(selectedSpaceItem)
  }, [mirrorGlobalSelection, selectedItemState, selectedSpaceItem, spaceId])

  useEffect(() => {
    if (previousSpaceIdRef.current === spaceId) {
      return
    }

    previousSpaceIdRef.current = spaceId
    hasTakenSelectionControlRef.current = false
    setSpaceMembers([])
    selectedItemVersionRef.current += 1
    setSelectedItemState(null)
  }, [setSpaceMembers, spaceId])

  const setSelectedItem: Dispatch<SetStateAction<SelectedSpaceItem>> =
    useCallback((value) => {
      hasTakenSelectionControlRef.current = true
      setSelectedItemState(currentItem => {
        const nextItem =
          typeof value === "function" ? value(currentItem) : value

        if (
          getSelectedItemKey(currentItem) !==
          getSelectedItemKey(nextItem)
        ) {
          selectedItemVersionRef.current += 1
        }

        return nextItem
      })
    }, [])

  const openItem = useCallback(
    async (
      item: SpaceItemDetailTarget<SpaceSelectableItemType>
    ): Promise<SelectedSpaceItem | null> => {
      hasTakenSelectionControlRef.current = true
      const requestVersion = selectedItemVersionRef.current + 1
      selectedItemVersionRef.current = requestVersion
      let data: SpaceItemDetailData<SpaceSelectableItemType> | null | undefined

      try {
        data = await queryClient.fetchQuery(
          spaceItemDetailQueryOptions(spaceId, item)
        )
      } catch (error) {
        if (!isSpaceQueryCancelledError(error)) {
          throw error
        }

        data = queryClient.getQueryData(
          spacesQueryKeys.itemDetail(spaceId, item.type, item.id)
        ) as SpaceItemDetailData<SpaceSelectableItemType> | null | undefined

        if (!data) {
          return null
        }
      }

      if (selectedItemVersionRef.current !== requestVersion) {
        return null
      }

      if (!data || data.space_id !== spaceId) {
        setSelectedItemState(null)
        return null
      }

      const nextSelectedItem = toSelectedSpaceItem(item, data)
      setSelectedItemState(nextSelectedItem)

      return nextSelectedItem
    },
    [queryClient, spaceId]
  )

  const refreshSpace = useCallback(async () => {
    let latestSpace: Tables<"spaces"> | null | undefined

    try {
      latestSpace = await queryClient.fetchQuery(
        spaceDetailQueryOptions(spaceId)
      )
    } catch (error) {
      if (!isSpaceQueryCancelledError(error)) {
        throw error
      }

      latestSpace =
        queryClient.getQueryData<Tables<"spaces">>(
          spacesQueryKeys.detail(spaceId)
        ) ?? (selectedSpace?.id === spaceId ? selectedSpace : null)
    }

    if (!latestSpace) {
      return null
    }

    setSelectedSpace(latestSpace)
    setSpaces((prev) => upsertSpaceIntoKnownList(prev, latestSpace) ?? prev)
    queryClient.setQueryData(
      spacesQueryKeys.list(latestSpace.workspace_id),
      (currentSpaces: Array<typeof latestSpace> | undefined) =>
        upsertSpaceIntoKnownList(currentSpaces, latestSpace)
    )
    void queryClient.invalidateQueries({
      queryKey: spacesQueryKeys.list(latestSpace.workspace_id)
    })

    return latestSpace
  }, [queryClient, selectedSpace, setSelectedSpace, setSpaces, spaceId])

  useEffect(() => {
    if (!spaceId) {
      return
    }

    void queryClient.prefetchQuery(spaceHierarchyQueryOptions(spaceId, false))
    void queryClient.prefetchQuery(spaceActivitiesQueryOptions(spaceId, 10))
    void queryClient.prefetchQuery(spaceWhiteboardsQueryOptions(spaceId))
  }, [queryClient, spaceId])

  useEffect(() => {
    const space = spaceQuery.data

    if (!space) {
      return
    }

    setSelectedSpace(space)
    setSpaces((prev) => upsertSpaceIntoKnownList(prev, space) ?? prev)

    const cachedListSpace = queryClient
      .getQueryData<Array<typeof space>>(
        spacesQueryKeys.list(space.workspace_id)
      )
      ?.find((listSpace) => listSpace.id === space.id)

    queryClient.setQueryData(
      spacesQueryKeys.list(space.workspace_id),
      (currentSpaces: Array<typeof space> | undefined) =>
        upsertSpaceIntoKnownList(currentSpaces, space)
    )

    if (!cachedListSpace || cachedListSpace.updated_at !== space.updated_at) {
      void queryClient.invalidateQueries({
        queryKey: spacesQueryKeys.list(space.workspace_id)
      })
    }

    if (workspaceSyncSpaceIdRef.current !== space.id) {
      if (!space.workspace_id || selectedWorkspace?.id === space.workspace_id) {
        workspaceSyncSpaceIdRef.current = space.id
      } else {
        const matchingWorkspace = workspaces.find(
          (workspace) => workspace.id === space.workspace_id
        )

        if (matchingWorkspace) {
          workspaceSyncSpaceIdRef.current = space.id
          setSelectedWorkspace(matchingWorkspace)
        }
      }
    }

    const currentItem = selectedItemRef.current

    if (currentItem?.data?.space_id && currentItem.data.space_id !== spaceId) {
      selectedItemVersionRef.current += 1
      setSelectedItemState(null)
    }
  }, [
    queryClient,
    selectedWorkspace?.id,
    setSelectedWorkspace,
    setSelectedSpace,
    setSpaces,
    spaceId,
    spaceQuery.data,
    workspaces
  ])

  useEffect(() => {
    if (membersQuery.data) {
      setSpaceMembers(membersQuery.data)
      return
    }

    if (membersQuery.error instanceof Error) {
      setSpaceMembers([])
    }
  }, [membersQuery.data, membersQuery.error, setSpaceMembers])

  const value = useMemo<SpacePageContextValue>(
    () => ({
      spaceId,
      space: currentSpace,
      members: currentMembers,
      selectedItem: selectedItemState,
      isLoading,
      isAuthError,
      errorMessage,
      setSelectedItem,
      openItem,
      refreshSpace
    }),
    [
      currentMembers,
      currentSpace,
      errorMessage,
      isAuthError,
      isLoading,
      openItem,
      refreshSpace,
      selectedItemState,
      spaceId
    ]
  )

  return (
    <SpacePageContext.Provider value={value}>
      {children}
    </SpacePageContext.Provider>
  )
}

export const useSpacePageContextOptional = () => {
  return useContext(SpacePageContext)
}

export const useSpacePageContext = () => {
  const context = useSpacePageContextOptional()

  if (!context) {
    throw new Error("useSpacePageContext must be used within SpacePageProvider")
  }

  return context
}
